/**
 * `useSessionTranscript` — Phase 4-6 resume hydration.
 *
 * Given a sessionId, fetches `/agent/runtime-sessions/:id/` (the staff
 * detail endpoint shipped in Phase 2) and converts the DB event rows
 * into the playground hook's `UIMessage[]` shape. When the fetch finishes,
 * `usePlaygroundChat` uses the result as its `initialMessages`.
 *
 * Mirrors the Core-side `historyReplay.ts` mapping but produces UI-
 * layer messages (with the `tool-<name>` part shape that
 * `usePlaygroundChat.applyEvent` already renders) rather than AI SDK
 * ModelMessages. Same walk-order + same tool-call pairing rules.
 *
 * Note: this hook is intentionally console-only (the staff endpoint
 * returns 403 to non-staff). Phase 8 adds a non-staff variant for
 * SubAgentChatView when production surfaces resume. Staff check is
 * not enforced here — the existing route guard covers it.
 */

import { useEffect, useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

const RuntimeEventSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  event_type: z.string(),
  data: z.record(z.string(), z.unknown()),
});

const SessionDetailSchema = z.object({
  // Permissive on the session header — the Phase 2 staff serializer
  // carries a lot of extra fields we don't need for replay, and we only
  // really consume `agent_name` + `runtime`. `is_playground` is optional
  // because the staff /agent/runtime-sessions/:id/ response doesn't
  // include it (the internal transcript endpoint does; the UI drawer
  // uses the trimmed /agent/my-sessions/ shape).
  session: z
    .object({
      id: z.string(),
      agent_name: z.string(),
      runtime: z.string(),
      is_playground: z.boolean().optional(),
    })
    .passthrough(),
  events: z.array(RuntimeEventSchema),
  events_truncated: z.boolean(),
  max_sequence: z.number(),
});

type RuntimeEvent = z.infer<typeof RuntimeEventSchema>;

function newId(role: "user" | "assistant"): string {
  return `${role}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

interface PartialToolPart {
  type: string; // "tool-<name>"
  toolCallId: string;
  state: "input-available" | "output-available" | "output-error";
  input?: unknown;
  output?: unknown;
}

/** Walk ordered events → UIMessage[]. Mirrors Core's historyReplay semantics. */
export function eventsToUIMessages(events: RuntimeEvent[]): UIMessage[] {
  // First pass: identify tool.use events without a matching tool.result.
  const answered = new Map<string, boolean>();
  for (const ev of events) {
    if (ev.event_type === "tool.use") {
      const id = typeof ev.data.id === "string" ? ev.data.id : undefined;
      if (id && !answered.has(id)) answered.set(id, false);
    } else if (ev.event_type === "tool.result") {
      const id = typeof ev.data.id === "string" ? ev.data.id : undefined;
      if (id) answered.set(id, true);
    }
  }

  const messages: UIMessage[] = [];
  let currentAssistant: UIMessage | undefined;
  const toolNameById = new Map<string, string>();

  const flushAssistant = (): void => {
    if (!currentAssistant) return;
    if (currentAssistant.parts.length > 0) messages.push(currentAssistant);
    currentAssistant = undefined;
  };

  const startAssistant = (): UIMessage => {
    if (!currentAssistant) {
      currentAssistant = { id: newId("assistant"), role: "assistant", parts: [] };
    }
    return currentAssistant;
  };

  for (const ev of events) {
    switch (ev.event_type) {
      case "user.message": {
        flushAssistant();
        const text = typeof ev.data.text === "string" ? ev.data.text : "";
        if (!text) break;
        messages.push({
          id: newId("user"),
          role: "user",
          parts: [{ type: "text", text }],
        });
        break;
      }
      case "model.delta": {
        const text = typeof ev.data.text === "string" ? ev.data.text : "";
        if (!text) break;
        const m = startAssistant();
        const last = m.parts[m.parts.length - 1];
        if (last && last.type === "text") {
          (last as { text: string }).text += text;
        } else {
          m.parts.push({ type: "text", text });
        }
        break;
      }
      case "tool.use": {
        const id = typeof ev.data.id === "string" ? ev.data.id : undefined;
        // The runtime event persister writes the tool name as
        // `data.toolName` (not `data.name`). Read both for safety so old
        // rows with either shape resolve to a real tool name instead of
        // collapsing to "unknown" in the transcript UI.
        const name =
          typeof ev.data.toolName === "string"
            ? ev.data.toolName
            : typeof ev.data.name === "string"
              ? ev.data.name
              : undefined;
        if (!id || !name) break;
        toolNameById.set(id, name);
        if (answered.get(id) === false) break; // drop dangling
        const m = startAssistant();
        m.parts.push({
          type: `tool-${name}`,
          toolCallId: id,
          state: "input-available",
          input: ev.data.input ?? {},
        } as unknown as UIMessage["parts"][number]);
        break;
      }
      case "tool.result": {
        const id = typeof ev.data.id === "string" ? ev.data.id : undefined;
        if (!id) break;
        const name = toolNameById.get(id) ?? "unknown";
        const isError = ev.data.isError === true;
        const m = startAssistant();
        const existing = m.parts.find((p) => {
          const pp = p as unknown as PartialToolPart;
          return pp.toolCallId === id;
        });
        if (existing) {
          const pp = existing as unknown as PartialToolPart;
          pp.state = isError ? "output-error" : "output-available";
          pp.output = ev.data.output;
        } else {
          // Tool result with no matching tool.use in-flight — attach a
          // complete part so the UI still shows the outcome.
          m.parts.push({
            type: `tool-${name}`,
            toolCallId: id,
            state: isError ? "output-error" : "output-available",
            input: {},
            output: ev.data.output,
          } as unknown as UIMessage["parts"][number]);
        }
        break;
      }
      case "span.model_request_end":
      case "session.done":
      case "session.error":
      case "session.terminated":
        flushAssistant();
        break;
      default:
        // session.attach, model.thinking, etc. — UX signals, no transcript effect.
        break;
    }
  }
  flushAssistant();
  return messages;
}

export interface UseSessionTranscriptResult {
  messages: UIMessage[] | undefined;
  agentName: string | undefined;
  runtime: string | undefined;
  loading: boolean;
  error: string | undefined;
}

export function useSessionTranscript(
  sessionId: string | undefined,
): UseSessionTranscriptResult {
  const [messages, setMessages] = useState<UIMessage[] | undefined>(undefined);
  const [agentName, setAgentName] = useState<string | undefined>(undefined);
  const [runtime, setRuntime] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!sessionId) {
      setMessages(undefined);
      setAgentName(undefined);
      setRuntime(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    apiFetch(`/agent/runtime-sessions/${sessionId}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((raw) => {
        if (cancelled) return;
        const parsed = SessionDetailSchema.parse(raw);
        setAgentName(parsed.session.agent_name);
        setRuntime(parsed.session.runtime);
        setMessages(eventsToUIMessages(parsed.events));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { messages, agentName, runtime, loading, error };
}
